/**
 * FolderScanJobSheet — the expanded view behind the floating progress chip.
 *
 * Tapping FolderScanProgressChip opens this: the full list of files in the
 * active ingest, each with its own live status (waiting / uploading / reading /
 * done / failed), plus the aggregate header. The job keeps running whether this
 * is open or closed — it's purely a "re-open the file list" surface.
 */

import React from 'react';
import {
  View,
  Text,
  Modal,
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import Icon from './Icon';
import { formatBytes } from '../utilities/diskScan';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ScanJob, ScanJobFile } from '../contexts/FolderScanJobContext';

interface Props {
  visible: boolean;
  onClose: () => void;
  job: ScanJob;
  /** Cancel + clear the whole job (also closes this sheet). */
  onDismissJob: () => void;
}

function iconForExt(ext: string): IconProp {
  if (ext === 'pdf') return ['fad', 'file-pdf'];
  return ['fad', 'file-lines'];
}

export default function FolderScanJobSheet({ visible, onClose, job, onDismissJob }: Props) {
  const theme = useTheme();
  const sheet = useSheetLayout({ desktopMaxWidth: 540 });
  const styles = makeStyles(theme);

  const who = job.projectName?.trim() ? job.projectName.trim() : 'this plan';
  const done = job.status === 'done';
  const uploading = job.status === 'uploading';

  const fraction = uploading
    ? (job.total ? job.uploaded / job.total : 0)
    : (job.total ? (job.processed + job.failed) / job.total : 0);
  const pct = Math.max(0, Math.min(100, Math.round(fraction * 100)));

  const title = done
    ? `Added ${job.processed} to ${who}`
    : uploading
      ? `Adding to ${who}`
      : `Reading ${who}'s files`;

  const renderStatus = (f: ScanJobFile) => {
    switch (f.status) {
      case 'processed':
        return <Icon icon={['fad', 'circle-check']} size={16} color={theme.colors.primary} />;
      case 'failed':
        return <Icon icon={['fas', 'xmark']} size={14} color={theme.colors.error} />;
      case 'uploading':
      case 'processing':
        return <ActivityIndicator size="small" color={theme.colors.primary} />;
      default:
        return <Text style={[styles.waiting, { color: theme.colors.muted }]}>Waiting</Text>;
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType={sheet.animationType}
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <Pressable style={[styles.overlay, sheet.overlayStyle]} onPress={onClose}>
        <Pressable style={[styles.sheet, { backgroundColor: theme.colors.card }, sheet.sheetStyle]} onPress={() => {}}>
          {sheet.showHandle && <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />}

          <View style={styles.header}>
            <Text style={[styles.title, { color: theme.colors.text }]} numberOfLines={1}>{title}</Text>
            <Text style={[styles.sub, { color: theme.colors.muted }]}>
              {done
                ? (job.failed > 0 ? `${job.processed} added · ${job.failed} failed` : 'Ellie finished reading them')
                : `${uploading ? job.uploaded : job.processed} of ${job.total} · ${pct}%`}
            </Text>
            <View style={[styles.track, { backgroundColor: theme.colors.border }]}>
              <View style={[styles.fill, { backgroundColor: theme.colors.primary, width: `${done ? 100 : pct}%` }]} />
            </View>
          </View>

          <ScrollView style={styles.list} contentContainerStyle={{ paddingBottom: 6 }}>
            {job.files.map((f) => (
              <View key={f.path} style={styles.fileRow}>
                <View style={[styles.fileIcon, { backgroundColor: theme.colors.surface }]}>
                  <Icon icon={iconForExt(f.ext)} size={16} color={theme.colors.primary} />
                </View>
                <View style={styles.fileText}>
                  <Text style={[styles.fileName, { color: theme.colors.text }]} numberOfLines={1}>{f.name}</Text>
                  <Text style={[styles.fileMeta, { color: theme.colors.muted }]} numberOfLines={1}>
                    {f.folder} · {formatBytes(f.size)}
                  </Text>
                </View>
                <View style={styles.statusCell}>{renderStatus(f)}</View>
              </View>
            ))}
          </ScrollView>

          <View style={[styles.footer, { borderTopColor: theme.colors.border }]}>
            <TouchableOpacity onPress={onClose} style={styles.footerBtn}>
              <Text style={[styles.footerText, { color: theme.colors.primary }]}>
                {done ? 'Done' : 'Minimize'}
              </Text>
            </TouchableOpacity>
            {!done && (
              <TouchableOpacity onPress={onDismissJob} style={styles.footerBtn}>
                <Text style={[styles.footerText, { color: theme.colors.muted }]}>Cancel</Text>
              </TouchableOpacity>
            )}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const makeStyles = (theme: ReturnType<typeof useTheme>) =>
  StyleSheet.create({
    overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
    sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingBottom: 30, maxHeight: '86%' },
    handle: { width: 36, height: 4, borderRadius: 2, alignSelf: 'center', marginTop: 10, marginBottom: 4 },
    header: { paddingHorizontal: 20, paddingTop: 8, paddingBottom: 12 },
    title: { fontSize: 18, fontWeight: '700' },
    sub: { fontSize: 13, marginTop: 4 },
    track: { height: 6, borderRadius: 999, overflow: 'hidden', marginTop: 12 },
    fill: { height: '100%', borderRadius: 999 },
    list: { maxHeight: 360, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: theme.colors.border },
    fileRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 20, paddingVertical: 9 },
    fileIcon: { width: 36, height: 36, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
    fileText: { flex: 1 },
    fileName: { fontSize: 14, fontWeight: '500' },
    fileMeta: { fontSize: 12, marginTop: 1 },
    statusCell: { width: 64, alignItems: 'flex-end', justifyContent: 'center' },
    waiting: { fontSize: 12 },
    footer: {
      flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
      paddingHorizontal: 24, paddingTop: 12, borderTopWidth: StyleSheet.hairlineWidth,
    },
    footerBtn: { paddingVertical: 8 },
    footerText: { fontSize: 15, fontWeight: '600' },
  });
